(() => {
  'use strict';
  const KEYS = Object.freeze({ finder: 'lbdb-watch-finder:v1', apple: 'lbdb-apple-watch:v1', nonapple: 'lbdb-nonapple-watch:v1' });
  const normalise = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const localPath = (root, path) => String(root || '/').replace(/\/?$/, '/') + path.replace(/^\//, '');
  const sameId = (a, b) => String(a || '').split('/').pop() === String(b || '').split('/').pop();
  const text = (template, values = {}) => String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|%\{\s*(\w+)\s*\}/g, (_, a, b) => String(values[a || b] ?? ''));
  const availableFirst = variants => variants.slice().sort((a, b) => Number(b.available === true) - Number(a.available === true) || Number(a.price || 0) - Number(b.price || 0));

  function canonicalProfile(profile, watches, apple) {
    if (profile?.version !== 1) return null;
    if (profile.brand === 'Apple') {
      const valid = apple?.canonicalProfile(profile);
      return valid ? { ...valid, brand: 'Apple' } : null;
    }
    const watch = (watches || []).find(w => w.id === profile.modelId && w.brand === profile.brand);
    return watch ? { version: 1, brand: watch.brand, modelId: watch.id } : null;
  }

  function matchProduct(product, profile, watches, engines, stockOnly = true) {
    if (!product || product.complete === false || !Array.isArray(product.options) || !Array.isArray(product.variants)) return null;
    const valid = canonicalProfile(profile, watches, engines.apple);
    if (!valid || (valid.brand === 'Apple' ? product.family !== 'apple' : product.family !== 'nonapple')) return null;
    if (valid.brand === 'Apple') {
      for (const variant of availableFirst(product.variants)) {
        if (stockOnly && variant.available !== true) continue;
        const proof = engines.apple.resolve(product, valid.modelId, valid.caseMm, variant.options, valid.route);
        if (['compatible', 'soldout'].includes(proof.status) && sameId(proof.variantId, variant.id)) return { product, variant, value: proof.value || '', system: 'apple' };
      }
      return null;
    }
    const watch = watches.find(w => w.id === valid.modelId);
    if (!watch || watch.verification === 'unconfirmed' || watch.system === 'unknown' || (watch.system === 'spring_bar_quick_release' && watch.width == null)) return null;
    const groups = (product.groups || []).filter(g => Array.isArray(g.watches) && g.watches.includes(watch.id) && g.system === watch.system && g.width === watch.width);
    const candidates = groups.flatMap(group => engines.nonapple.authorisedVariants(product, group).map(variant => ({ variant, group })));
    candidates.sort((a, b) => Number(b.variant.available === true) - Number(a.variant.available === true) || Number(a.variant.price || 0) - Number(b.variant.price || 0));
    const candidate = candidates.find(({ variant }) => !stockOnly || variant.available === true);
    if (!candidate) return null;
    const { variant, group } = candidate;
    const proof = engines.nonapple.resolve(product, watches, valid.modelId, variant.options);
    if (!['compatible', 'soldout'].includes(proof.status)) return null;
    return { product, variant, value: group.position ? variant.options[group.position - 1] : '', system: group.system };
  }

  function filterProducts(products, profile, watches, engines, stockOnly, sort) {
    const seen = new Set();
    const matched = [];
    for (const product of products || []) {
      if (seen.has(String(product.id))) continue;
      seen.add(String(product.id));
      const proof = matchProduct(product, profile, watches, engines, stockOnly);
      if (proof) matched.push(proof);
    }
    if (sort === 'price_low' || sort === 'price_high') matched.sort((a, b) => (a.variant.price - b.variant.price) * (sort === 'price_high' ? -1 : 1));
    return matched;
  }

  async function collectCatalog(fetchPage, family) {
    const first = await fetchPage(1);
    const validPage = (data, number) => data && data.family === family && Number(data.page) === number && Number.isInteger(data.pages) && data.pages >= 1 && data.pages <= 30 && Array.isArray(data.products);
    if (!validPage(first, 1)) throw new Error('Invalid catalog');
    const pages = [first];
    for (let start = 2; start <= first.pages; start += 3) {
      const numbers = Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, index) => start + index);
      const batch = await Promise.all(numbers.map(async number => {
        const page = await fetchPage(number);
        if (!validPage(page, number) || page.pages !== first.pages || page.total !== first.total) throw new Error('Catalog changed or incomplete');
        return page;
      }));
      pages.push(...batch);
    }
    const seen = new Set();
    const products = [];
    for (const page of pages) for (const product of page.products) {
      if (!product || product.family !== family || !product.id || !product.handle) throw new Error('Invalid product');
      if (seen.has(String(product.id))) throw new Error('Duplicate pagination');
      seen.add(String(product.id)); products.push(product);
    }
    return products;
  }

  class WatchFinder extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._generation = 0;
      this._catalogs = new Map();
      this._memory = {};
      this._profile = null;
      this._brand = '';
      this._watches = [];
      this._engines = { apple: window.LBDBAppleWatchSizing, nonapple: window.LBDBNonAppleWatchSizing };
      try { this._i18n = JSON.parse(this.querySelector('[data-wf-i18n]').textContent); } catch (_) { return; }
      if (!this._engines.apple || !this._engines.nonapple) return;
      const find = selector => this.querySelector('[data-wf-' + selector + ']');
      this._nodes = {};
      for (const name of ['interactive', 'static', 'model-field', 'search-field', 'search', 'suggestions', 'model-toggle', 'other', 'apple-field', 'apple', 'case-field', 'case-grid', 'selected', 'selected-name', 'view', 'results', 'heading', 'count', 'toolbar', 'stock', 'sort', 'status', 'message', 'retry', 'skeletons', 'products', 'more', 'disclaimer', 'help', 'help-toggle']) this._nodes[name] = find(name);
      this._nodes.interactive.hidden = false; this._nodes.static.hidden = true;
      this._listen(this, 'click', event => {
        const brand = event.target.closest('[data-wf-brand]');
        const size = event.target.closest('[data-wf-case]');
        const choice = event.target.closest('[data-wf-choice]');
        if (brand) this._chooseBrand(brand.dataset.wfBrand);
        if (size) this._chooseAppleCase(Number(size.dataset.wfCase));
        if (choice) this._chooseWatch(choice.dataset.wfChoice);
      });
      this._listen(this._nodes.search, 'input', () => { this._profile = null; this._resetResults(); this._suggest(); });
      this._listen(this._nodes.search, 'focus', () => this._suggest());
      this._listen(this._nodes.search, 'click', () => this._suggest());
      this._listen(this._nodes['model-toggle'], 'click', () => { if (!this._nodes.suggestions.hidden) this._closeSuggestions(); else { this._nodes.search.focus({ preventScroll: true }); this._suggest(); } });
      this._listen(this._nodes.search, 'keydown', event => this._key(event));
      this._listen(this._nodes.apple, 'change', () => { this._profile = null; this._resetResults(); this._showCases(); });
      this._listen(this._nodes.stock, 'change', () => this._updateResults());
      this._listen(this._nodes.sort, 'change', () => this._updateResults());
      this._listen(this._nodes.more, 'click', () => this._appendCards(this._generation));
      this._listen(this._nodes.retry, 'click', () => { const generation = this._generation; this._state('loading'); this._catalogs.clear(); this._registryPromise = null; this._prepareBrand().then(() => { if (generation === this._generation) this._updateResults(); }).catch(() => { if (generation === this._generation) this._state('error'); }); });
      this._listen(this._nodes.view, 'click', () => this._nodes.results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' }));
      this._listen(this._nodes['help-toggle'], 'click', () => { const help = this._nodes.help; help.hidden = !help.hidden; this._nodes['help-toggle'].setAttribute('aria-expanded', String(!help.hidden)); });
      this._listen(document, 'click', event => { if (!this._nodes['search-field'].contains(event.target)) this._closeSuggestions(); });
      this._listen(window, 'pageshow', event => { if (event.persisted && (!this._brand || this._profile)) this._restore(); });
      this._fillAppleModels();
      this._restore();
    }
    disconnectedCallback() { this._connected = false; this._generation++; this._abort?.abort(); }
    _listen(node, event, callback) { node?.addEventListener(event, callback, { signal: this._abort.signal }); }
    _text(key, values) { return text(this._i18n[key], values); }
    _read(key) { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return this._memory[key] || null; } }
    _write(key, value) { this._memory[key] = value; try { sessionStorage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; } }
    _save(profile) {
      this._profile = profile;
      this._storageAvailable = this._write(KEYS.finder, profile);
      if (profile.brand === 'Apple') {
        const { brand, ...apple } = profile; this._write(KEYS.apple, apple);
        document.dispatchEvent(new CustomEvent('lbdb:apple-watch-change', { detail: { source: this } }));
      } else {
        this._write(KEYS.nonapple, profile);
        document.dispatchEvent(new CustomEvent('lbdb:nonapple-watch-change', { detail: { source: this } }));
      }
      this._updateResults();
    }
    async _restore() {
      const own = this._read(KEYS.finder);
      const nonapple = this._read(KEYS.nonapple);
      const apple = this._read(KEYS.apple);
      const profile = own || nonapple || (apple ? { ...apple, brand: 'Apple' } : null);
      if (!profile || profile.version !== 1) return;
      const generation = this._generation;
      try {
        if (profile.brand !== 'Apple') await this._loadRegistry();
        if (!this._connected || generation !== this._generation) return;
        const valid = canonicalProfile(profile, this._watches, this._engines.apple);
        if (!valid) return;
        this._chooseBrand(valid.brand, true);
        if (valid.brand === 'Apple') {
          this._nodes.apple.value = valid.route === 'back' ? '__back' : valid.modelId;
          this._showCases(); this._profile = valid; this._syncCases();
        } else { this._profile = valid; this._nodes.search.value = this._watches.find(w => w.id === valid.modelId).name; }
        this._updateResults();
      } catch (_) { this._state('error'); }
    }
    async _fetch(url) {
      const abort = new AbortController();
      const stop = () => abort.abort();
      this._abort.signal.addEventListener('abort', stop, { once: true });
      const timeout = setTimeout(stop, 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: abort.signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return await response.text();
      } finally { clearTimeout(timeout); this._abort.signal.removeEventListener('abort', stop); }
    }
    _json(html, selector) {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (!node) throw new Error('Missing section');
      return JSON.parse(node.textContent);
    }
    _loadRegistry() {
      if (!this._registryPromise) this._registryPromise = this._fetch(this.dataset.registry).then(raw => {
        const data = JSON.parse(raw);
        if (!Array.isArray(data.watches) || !data.watches.length) throw new Error('Invalid registry');
        this._watches = data.watches; if (this._nodes.search === document.activeElement && this._brand !== 'Apple') this._suggest(); return this._watches;
      }).catch(error => { this._registryPromise = null; throw error; });
      return this._registryPromise;
    }
    _family() { return this._brand === 'Apple' ? 'apple' : 'nonapple'; }
    _catalog(family) {
      if (!this._catalogs.has(family)) {
        const base = family === 'apple' ? this.dataset.appleCatalog : this.dataset.nonappleCatalog;
        if (!base) return Promise.reject(new Error('Missing collection'));
        const promise = collectCatalog(async number => {
          const url = new URL(base, location.origin);
          if (url.origin !== location.origin) throw new Error('External catalog');
          url.searchParams.set('section_id', 'lbdb-watch-finder-catalog'); url.searchParams.set('page', String(number)); url.searchParams.set('sort_by', 'best-selling');
          return this._json(await this._fetch(url.href), '[data-watch-finder-catalog]');
        }, family).catch(error => { this._catalogs.delete(family); throw error; });
        this._catalogs.set(family, promise);
      }
      return this._catalogs.get(family);
    }
    async _prepareBrand() { if (this._brand !== 'Apple') await this._loadRegistry(); if (this._brand !== '__other') await this._catalog(this._family()); }
    _chooseBrand(brand, restoring = false) {
      this._brand = brand; this._profile = null;
      this._resetResults();
      for (const node of this.querySelectorAll('[data-wf-brand]')) node.setAttribute('aria-pressed', String(node.dataset.wfBrand === brand));
      const apple = brand === 'Apple';
      this._nodes['model-field'].hidden = brand === '__other';
      this._nodes.results.hidden = brand === '__other';
      if (this._nodes.other) this._nodes.other.hidden = brand !== '__other';
      const label = this._nodes['model-field'].querySelector('label');
      if (label) label.htmlFor = apple ? this._nodes.apple.id : this._nodes.search.id;
      this._nodes.help.hidden = true; this._nodes['help-toggle'].setAttribute('aria-expanded', 'false');
      this._nodes['search-field'].hidden = apple || brand === '__other'; this._nodes['apple-field'].hidden = !apple;
      this._nodes.search.value = ''; this._nodes.apple.value = ''; this._nodes['case-field'].hidden = true;
      this._closeSuggestions(); this._progress();
      if (brand === '__other') { this._state('verify'); return; }
      const generation = this._generation;
      this._prepareBrand().then(() => { if (this._brand === brand && !restoring && !this._profile && this._nodes.search === document.activeElement) this._suggest(); }).catch(() => { if (this._brand === brand && (generation === this._generation || !this._profile)) this._state('error'); });
    }
    _fillAppleModels() {
      const select = this._nodes.apple;
      for (const model of this._engines.apple.MODEL_DEFINITIONS) {
        const option = document.createElement('option'); option.value = model.id;
        option.textContent = model.family === 'original' ? this._text('first_generation') : model.family === 'series' ? this._text('series') + ' ' + model.number : (model.family === 'se' ? 'SE ' : 'Ultra ') + model.number;
        select.append(option);
      }
      const back = document.createElement('option'); back.value = '__back'; back.textContent = this._text('by_size'); select.append(back);
    }
    _showCases() {
      const modelId = this._nodes.apple.value;
      const cases = modelId === '__back' ? this._engines.apple.ACTUAL_CASE_SIZES : this._engines.apple.getAllowedCases(modelId);
      this._nodes['case-field'].hidden = !cases.length;
      for (const node of this.querySelectorAll('[data-wf-case]')) { node.hidden = !cases.includes(Number(node.dataset.wfCase)); node.setAttribute('aria-pressed', 'false'); }
      if (cases.length === 1) this._chooseAppleCase(cases[0]);
    }
    _syncCases() { for (const node of this.querySelectorAll('[data-wf-case]')) node.setAttribute('aria-pressed', String(Number(node.dataset.wfCase) === this._profile?.caseMm)); }
    _chooseAppleCase(caseMm) {
      const modelId = this._nodes.apple.value;
      const profile = canonicalProfile({ version: 1, brand: 'Apple', route: modelId === '__back' ? 'back' : 'model', modelId: modelId === '__back' ? '' : modelId, caseMm }, this._watches, this._engines.apple);
      if (!profile) return;
      this._profile = profile; this._syncCases(); this._save(profile);
    }
    _suggest() {
      if (!this._brand || this._brand === 'Apple' || this._brand === '__other') return;
      const query = normalise(this._nodes.search.value).split(' ').filter(Boolean);
      const matches = this._watches.filter(w => w.brand === this._brand && query.every(word => normalise(w.name).includes(word)));
      const list = this._nodes.suggestions; list.replaceChildren(); this._activeIndex = -1;
      for (const watch of matches) {
        const button = document.createElement('button'); button.type = 'button'; button.tabIndex = -1; button.setAttribute('role', 'option'); button.setAttribute('aria-selected', 'false');
        button.id = list.id + '-' + watch.id; button.dataset.wfChoice = watch.id; button.textContent = watch.name; list.append(button);
      }
      if (!matches.length) { const message = document.createElement('p'); message.textContent = this._text('no_matches'); list.append(message); }
      list.hidden = false; this._nodes.search.setAttribute('aria-expanded', 'true'); this._nodes.search.removeAttribute('aria-activedescendant');
      this._nodes['model-toggle']?.setAttribute('aria-expanded', 'true');
    }
    _key(event) {
      if (event.key === 'Escape') { event.preventDefault(); this._closeSuggestions(); return; }
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      if (this._nodes.suggestions.hidden) this._suggest();
      const options = Array.from(this._nodes.suggestions.querySelectorAll('[data-wf-choice]'));
      if (!options.length) return;
      event.preventDefault();
      if (event.key === 'Enter') { const option = options[this._activeIndex] || (options.length === 1 ? options[0] : null); if (option) this._chooseWatch(option.dataset.wfChoice); return; }
      this._activeIndex = (this._activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      for (const [index, option] of options.entries()) option.setAttribute('aria-selected', String(index === this._activeIndex));
      const active = options[this._activeIndex]; this._nodes.search.setAttribute('aria-activedescendant', active.id); active.scrollIntoView({ block: 'nearest' });
    }
    _closeSuggestions() { this._nodes.suggestions.hidden = true; this._nodes.search.setAttribute('aria-expanded', 'false'); this._nodes.search.removeAttribute('aria-activedescendant'); this._nodes['model-toggle']?.setAttribute('aria-expanded', 'false'); }
    _chooseWatch(id) {
      const watch = this._watches.find(w => w.id === id && w.brand === this._brand);
      if (!watch) return;
      this._nodes.search.value = watch.name; this._closeSuggestions(); this._save({ version: 1, brand: watch.brand, modelId: watch.id });
    }
    _name() {
      if (!this._profile) return '';
      if (this._profile.brand !== 'Apple') return this._watches.find(w => w.id === this._profile.modelId )?.name || '';
      const model = this._nodes.apple.selectedOptions[0]?.textContent;
      return 'Apple Watch' + (this._profile.route === 'model' ? ' ' + model : '') + ' · ' + this._profile.caseMm + ' mm';
    }
    _progress() {
      for (const node of this.querySelectorAll('[data-wf-step]')) node.dataset.complete = String(Number(node.dataset.wfStep) === 1 ? !!this._brand : Number(node.dataset.wfStep) === 2 ? !!this._profile : !!this._matches?.length && !!this._nodes.products.children.length);
    }
    _resetResults() {
      this._generation++; this._matches = []; this._rendered = 0; this._loadingCards = false;
      this._nodes.products.replaceChildren(); this._nodes.count.textContent = ''; this._nodes.more.hidden = true; this._nodes.toolbar.hidden = true; this._nodes.selected.hidden = true; this._nodes.view.hidden = true; this._nodes.disclaimer.hidden = true;
      this._state('choose'); this._progress();
    }
    _state(state) {
      const node = this._nodes.status; node.dataset.state = state; node.hidden = state === 'ready';
      this._nodes.message.textContent = this._text(state === 'loading' ? 'loading' : state === 'error' ? 'error' : state === 'verify' ? (this._brand === '__other' ? 'other_note' : 'verify') : state === 'empty' ? 'empty' : state === 'stock_empty' ? 'stock_empty' : 'choose');
      this._nodes.retry.hidden = state !== 'error'; this._nodes.skeletons.hidden = state !== 'loading';
      this._nodes.results.setAttribute('aria-busy', String(state === 'loading'));
    }
    async _updateResults() {
      const profile = this._profile;
      this._resetResults(); this._profile = profile;
      if (!profile) return;
      const generation = this._generation;
      const watch = this._watches.find(w => w.id === profile.modelId);
      const uncertain = profile.brand !== 'Apple' && (!watch || watch.verification === 'unconfirmed' || watch.system === 'unknown' || (watch.system === 'spring_bar_quick_release' && watch.width == null));
      this._nodes['selected-name'].textContent = this._name(); this._nodes.selected.querySelector('p').hidden = this._storageAvailable === false; this._nodes.selected.hidden = uncertain; this._progress();
      if (uncertain) { this._state('verify'); return; }
      this._state('loading');
      try {
        const catalog = await this._catalog(this._family());
        if (!this._connected || generation !== this._generation) return;
        this._matches = filterProducts(catalog, profile, this._watches, this._engines, this._nodes.stock.checked, this._nodes.sort.value);
        this._nodes.toolbar.hidden = false;
        this._nodes.count.textContent = this._text('count', { count: this._matches.length });
        if (!this._matches.length) { const all = filterProducts(catalog, profile, this._watches, this._engines, false); this._state(all.length ? 'stock_empty' : 'empty'); return; }
        await this._appendCards(generation);
      } catch (_) { if (this._connected && generation === this._generation) this._state('error'); }
    }
    async _appendCards(generation) {
      if (this._loadingCards || generation !== this._generation) return;
      const wanted = this._matches.slice(this._rendered, this._rendered + 12);
      if (!wanted.length) return;
      this._loadingCards = true; this._nodes.more.disabled = true;
      try {
        const url = new URL(localPath(this.dataset.root, 'search'), location.origin); url.searchParams.set('section_id', 'lbdb-watch-finder-cards'); url.searchParams.set('type', 'product'); url.searchParams.set('q', wanted.map(match => match.product.handle + '~' + match.variant.id).join('|'));
        const doc = new DOMParser().parseFromString(await this._fetch(url.href), 'text/html');
        if (!this._connected || generation !== this._generation) return;
        const cards = Array.from(doc.querySelectorAll('[data-wf-product]'));
        if (cards.length !== wanted.length) throw new Error('Incomplete cards');
        const fragment = document.createDocumentFragment();
        for (const [index, card] of cards.entries()) {
          const match = wanted[index];
          const data = JSON.parse(card.querySelector('[data-wf-card]').textContent);
          if (!sameId(data.id, match.product.id) || !sameId(data.variantId, match.variant.id) || (this._nodes.stock.checked && data.available !== true) || data.options.join('\u0000') !== match.variant.options.join('\u0000')) throw new Error('Stale card');
          for (const script of card.querySelectorAll('script')) script.remove();
          const badge = card.querySelector('[data-wf-match]'); badge.textContent = match.value ? this._text('card_match', { value: match.value }) : this._text('fixed'); badge.dataset.soldout = String(data.available !== true);
          fragment.append(document.importNode(card, true));
        }
        this._nodes.products.append(fragment); this._rendered += wanted.length;
        this._nodes.more.hidden = this._rendered >= this._matches.length;
        this._nodes.view.hidden = false; this._nodes.disclaimer.hidden = false; this._state('ready'); this._progress();
      } catch (_) { if (this._connected && generation === this._generation) this._state('error'); }
      finally { if (generation === this._generation) { this._loadingCards = false; this._nodes.more.disabled = false; } }
    }
  }
  const exported = { KEYS, normalise, localPath, canonicalProfile, matchProduct, filterProducts, collectCatalog, WatchFinder };
  if (typeof module !== 'undefined' && module.exports) module.exports = exported;
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lbdb-watch-finder')) window.customElements.define('lbdb-watch-finder', WatchFinder);
})();
